import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { UserCircleIcon, PhotoIcon, KeyIcon, CheckIcon, XMarkIcon, LanguageIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext.tsx';
import { authAPI, usersAPI } from '../services/api.ts';
import ImageCropModal from '../components/ImageCropModal.tsx';
import languageService from '../services/languageService.ts';

export const Profile: React.FC = () => {
  const { user, customer, refreshProfile } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Profile form state
  const [profileForm, setProfileForm] = useState({
    name: user?.name || '',
    email: user?.email || ''
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState<string | null>(null);
  
  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  
  // Language preference state
  const [languagePreference, setLanguagePreference] = useState(user?.language_preference || 'en');
  const [languageLoading, setLanguageLoading] = useState(false);
  const [languageError, setLanguageError] = useState<string | null>(null);
  const [languageSuccess, setLanguageSuccess] = useState<string | null>(null);
  
  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  // Initialize language preference from user data or current language
  useEffect(() => {
    if (user?.language_preference) {
      setLanguagePreference(user.language_preference);
      console.log('Profile: Setting language preference from user data:', user.language_preference);
    } else {
      setLanguagePreference(languageService.getCurrentLanguage());
      console.log('Profile: Setting language preference from current language:', languageService.getCurrentLanguage());
    }
    
    // Debug language service state
    languageService.debug();
  }, [user]);

  // Apply dark mode theme to document
  useEffect(() => {
    const htmlElement = document.documentElement;
    const theme = darkMode ? 'business' : 'corporate';
    console.log('Profile.tsx: Setting theme to:', theme, 'darkMode:', darkMode);
    htmlElement.setAttribute('data-theme', theme);
    if (darkMode) {
      htmlElement.classList.add('dark');
    } else {
      htmlElement.classList.remove('dark');
    }
    console.log('Profile.tsx: Final HTML attributes:', {
      'data-theme': htmlElement.getAttribute('data-theme'),
      'class': htmlElement.className
    });
    // Save to localStorage for persistence
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);
  
  // Profile picture state
  const [pictureLoading, setPictureLoading] = useState(false);
  const [pictureError, setPictureError] = useState<string | null>(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileLoading(true);
    setProfileError(null);
    setProfileSuccess(null);

    try {
      await authAPI.updateProfile(profileForm);
      await refreshProfile();
      setProfileSuccess('Profile updated successfully!');
    } catch (error: any) {
      setProfileError(error.response?.data?.error || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordError(null);
    setPasswordSuccess(null);

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match');
      setPasswordLoading(false);
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      setPasswordLoading(false);
      return;
    }

    try {
      await authAPI.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });
      setPasswordSuccess('Password changed successfully!');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error: any) {
      setPasswordError(error.response?.data?.error || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleLanguageChange = async (newLanguage: string) => {
    setLanguageLoading(true);
    setLanguageError(null);
    setLanguageSuccess(null);

    try {
      await usersAPI.updateLanguage(newLanguage);
      setLanguagePreference(newLanguage);
      setLanguageSuccess('Language preference updated successfully!');
      
      // Update the language immediately
      languageService.updateLanguage(newLanguage);
      
      // Refresh user profile to get updated language preference
      await refreshProfile();
    } catch (error: any) {
      setLanguageError(error.response?.data?.error || 'Failed to update language preference');
    } finally {
      setLanguageLoading(false);
    }
  };

  const handleDarkModeToggle = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    console.log('Dark mode toggled to:', newDarkMode);
    console.log('HTML data-theme:', document.documentElement.getAttribute('data-theme'));
  };

  const handleProfilePictureClick = () => {
    fileInputRef.current?.click();
  };

  const handleProfilePictureChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setPictureError('Please select an image file');
      return;
    }

    // Validate file size (10MB - we'll compress after cropping)
    if (file.size > 10 * 1024 * 1024) {
      setPictureError('Image must be less than 10MB');
      return;
    }

    setPictureError(null);

    // Create preview URL for cropping
    const reader = new FileReader();
    reader.onload = () => {
      setSelectedImage(reader.result as string);
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCropComplete = async (croppedImageFile: File) => {
    setPictureLoading(true);
    setPictureError(null);

    try {
      await authAPI.uploadProfilePicture(croppedImageFile);
      await refreshProfile();
      setShowCropModal(false);
      setSelectedImage(null);
    } catch (error: any) {
      setPictureError(error.response?.data?.error || 'Failed to upload profile picture');
    } finally {
      setPictureLoading(false);
    }
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setSelectedImage(null);
    setPictureError(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center">
        <UserCircleIcon className="h-8 w-8 text-primary-600 mr-3" />
        <h1 className="text-2xl font-bold text-gray-900">Profile Settings</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Picture */}
        <div className="card">
          <div className="card-body text-center">
            <h2 className="text-lg font-semibold mb-4">Profile Picture</h2>
            
            <div className="relative inline-block">
              {user?.profile_picture_url ? (
                <img
                  src={user.profile_picture_url}
                  alt="Profile"
                  className="w-24 h-24 rounded-full object-cover mx-auto"
                />
              ) : (
                <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto">
                  <UserCircleIcon className="h-16 w-16 text-gray-400" />
                </div>
              )}
              
              {pictureLoading && (
                <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                </div>
              )}
            </div>

            <button
              onClick={handleProfilePictureClick}
              disabled={pictureLoading}
              className="mt-5 btn btn-primary"
            >
              <PhotoIcon className="h-4 w-4 mr-2" />
              {pictureLoading ? 'Uploading...' : 'Change Picture'}
            </button>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleProfilePictureChange}
              className="hidden"
            />

            {pictureError && (
              <div className="mt-2 text-sm text-red-600">{pictureError}</div>
            )}
          </div>
        </div>

        {/* Profile Information */}
        <div className="card">
          <div className="card-body">
            <h2 className="text-lg font-semibold mb-4">Profile Information</h2>
            
            {profileSuccess && (
              <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3 flex items-center">
                <CheckIcon className="h-4 w-4 text-green-600 mr-2" />
                <span className="text-sm text-green-600">{profileSuccess}</span>
              </div>
            )}

            {profileError && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center">
                <XMarkIcon className="h-4 w-4 text-red-600 mr-2" />
                <span className="text-sm text-red-600">{profileError}</span>
              </div>
            )}

            <form onSubmit={handleProfileSubmit} className="space-y-4">
              <div>
                <label className="label">Full Name</label>
                <input
                  type="text"
                  className="input"
                  value={profileForm.name}
                  onChange={(e) => setProfileForm(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>

              <div>
                <label className="label">Email Address</label>
                <input
                  type="email"
                  className="input"
                  value={profileForm.email}
                  onChange={(e) => setProfileForm(prev => ({ ...prev, email: e.target.value }))}
                  required
                />
              </div>

              <div>
                <label className="label">Role</label>
                <input
                  type="text"
                  className="input"
                  value={user?.role || ''}
                  disabled
                />
              </div>

              <button
                type="submit"
                disabled={profileLoading}
                className="btn btn-primary btn-wide"
              >
                {profileLoading ? 'Updating...' : 'Update Profile'}
              </button>
            </form>

            {/* Dark Mode Section */}
            <div className="border-t border-gray-200 pt-6 mt-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
                Dark Mode
              </h3>
              
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-700">Enable Dark Mode</label>
                  <p className="text-xs text-gray-500">Switch between light and dark themes</p>
                </div>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={darkMode}
                    onChange={handleDarkModeToggle}
                    className="sr-only"
                  />
                  <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    darkMode ? 'bg-blue-600' : 'bg-gray-200'
                  }`}>
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      darkMode ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </div>
                </label>
              </div>
              
              <div className="text-xs text-gray-500 mt-2">
                <p>Current theme: <span className="font-medium">{darkMode ? 'Dark' : 'Light'}</span></p>
              </div>
            </div>
          </div>
        </div>

        {/* Change Password */}
        <div className="card">
          <div className="card-body">
            <h2 className="text-lg font-semibold mb-4 flex items-center">
              <KeyIcon className="h-5 w-5 mr-2" />
              Change Password
            </h2>

            {passwordSuccess && (
              <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3 flex items-center">
                <CheckIcon className="h-4 w-4 text-green-600 mr-2" />
                <span className="text-sm text-green-600">{passwordSuccess}</span>
              </div>
            )}

            {passwordError && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center">
                <XMarkIcon className="h-4 w-4 text-red-600 mr-2" />
                <span className="text-sm text-red-600">{passwordError}</span>
              </div>
            )}

            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <label className="label">Current Password</label>
                <input
                  type="password"
                  className="input"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                  required
                />
              </div>

              <div>
                <label className="label">New Password</label>
                <input
                  type="password"
                  className="input"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                  required
                  minLength={6}
                />
              </div>

              <div>
                <label className="label">Confirm New Password</label>
                <input
                  type="password"
                  className="input"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  required
                  minLength={6}
                />
              </div>

              <button
                type="submit"
                disabled={passwordLoading}
                className="btn btn-primary btn-wide"
              >
                {passwordLoading ? 'Changing...' : 'Change Password'}
              </button>
            </form>
          

          {/* Language Preference Section */}
          <div className="border-t border-gray-200 pt-6 mt-6">
           

            <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
            <LanguageIcon className="h-5 w-5 mr-2" />
               Language Preferences
              </h3>

            {languageSuccess && (
             
              <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3 flex items-center">
                <CheckIcon className="h-4 w-4 text-green-600 mr-2" />
                <span className="text-sm text-green-600">{languageSuccess}</span>
              </div>
             
            )}

            {languageError && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center">
                <XMarkIcon className="h-4 w-4 text-red-600 mr-2" />
                <span className="text-sm text-red-600">{languageError}</span>
              </div>
            )}

            <div className="space-y-4">
              <div>
                
                <select
                  className="select"
                  value={languagePreference}
                  onChange={(e) => handleLanguageChange(e.target.value)}
                  disabled={languageLoading}
                >
                  <option value="en">English</option>
                  <option value="es">Español</option>
                </select>
              </div>

              <div className="text-sm text-gray-600">
                <p>Your language preference will be saved and applied across all pages.</p>
                <p>Current language: <span className="font-medium">{languagePreference === 'en' ? 'English' : 'Español'}</span></p>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>


      {/* Account Information */}
      <div className="card">
        <div className="card-body">
          <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold mb-4">Account Information</h2>
          {user?.role === 'admin' && (
                      
                      <Link
                        to="/app/subscription"
                        className="btn btn-secondary inline-flex items-center"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                        Manage Subscription
                      </Link>
                 
                  )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Account Created:</span>
              <p className="text-gray-600 mt-1">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
              </p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Last Updated:</span>
              <p className="text-gray-600 mt-1">
                {user?.updated_at ? new Date(user.updated_at).toLocaleDateString() : 'N/A'}
              </p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Account Status:</span>
              <p className="text-gray-600 mt-1">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  customer?.status === 'free' 
                    ? 'bg-blue-100 text-blue-800'
                    : customer?.status === 'active'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-base-200 text-gray-800'
                }`}>
                  {customer?.status === 'free' ? 'Free Edition' : customer?.status === 'active' ? 'Active' : 'Inactive'}
                </span>
              </p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Subscription Plan:</span>
              <p className="text-gray-600 mt-1">
                {customer?.subscription_plan ? (
                  <span className="font-medium">
                    {customer.subscription_plan === 'free' ? 'Free Edition' : 
                     customer.subscription_plan === 'teams' ? 'Teams Edition' :
                     customer.subscription_plan === 'business' ? 'Business Edition' :
                     customer.subscription_plan === 'unlimited' ? 'Unlimited' :
                     customer.subscription_plan}
                    {customer.subscription_start_date && (
                      <span className="text-xs text-gray-500 block">
                        Started {new Date(customer.subscription_start_date).toLocaleDateString()}
                      </span>
                    )}
                    {customer.subscription_end_date && (
                      <span className="text-xs text-gray-500 block">
                        Renews {new Date(customer.subscription_end_date).toLocaleDateString()}
                      </span>
                    )}
                    {customer.subscription_canceled_at && (
                      <span className="text-xs text-red-500 block">
                        ⚠️ Will downgrade at renewal
                      </span>
                    )}
                  </span>
                ) : 'No subscription'}
              </p>
               {/* Subscription Management Link for Admin Users */}
          
            </div>
           
            
          </div>
          
       
        </div>
      
      
      </div>

      {/* Image Crop Modal */}
      {selectedImage && (
        <ImageCropModal
          isOpen={showCropModal}
          onClose={handleCropCancel}
          onCropComplete={handleCropComplete}
          imageSrc={selectedImage}
          loading={pictureLoading}
        />
      )}
    </div>
  );
};
